import React, { useMemo, useState } from 'react';
import BankCard from '../components/BankCard';
import CardFormModal from '../components/CardFormModal';
import ConfirmModal from '../components/ConfirmModal';
import useCards from '../hooks/useCards';
import useGridColumns from '../hooks/useGridColumns';
import { createCard, deleteCard, updateCard } from '../services/cardsApi';

const CardsList = () => {
  const { cards, loading, error, refetch } = useCards();
  const [search, setSearch] = useState('');
  const [formModal, setFormModal] = useState({ open: false, mode: 'add', card: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, card: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const gridColumns = useGridColumns();
  const gridStyle = {
    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
  };

  const filteredCards = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return cards;

    return cards.filter((card) =>
      card.title.toLowerCase().includes(query)
    );
  }, [cards, search]);

  const openAddForm = () => {
    setFormError(null);
    setFormModal({ open: true, mode: 'add', card: null });
  };

  const openEditForm = (card) => {
    setFormError(null);
    setFormModal({ open: true, mode: 'edit', card });
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModal({ open: false, mode: 'add', card: null });
    setFormError(null);
  };

  const openDeleteModal = (card) => {
    setDeleteError(null);
    setDeleteModal({ open: true, card });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModal({ open: false, card: null });
    setDeleteError(null);
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      setFormError(null);

      if (formModal.mode === 'edit') {
        await updateCard(formModal.card.id, formData);
      } else {
        await createCard(formData);
      }

      await refetch();
      closeFormModal();
    } catch (err) {
      setFormError(err.message || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.card?.id) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteCard(deleteModal.card.id);
      await refetch();
      closeDeleteModal();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete card');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <header className="mb-4">
        <div className="flex sm:hidden gap-2 items-center w-full">
          <div className="relative flex-1 min-w-0">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
              aria-label="Search cards"
            />
          </div>
          <button
            type="button"
            onClick={openAddForm}
            className="inline-flex items-center justify-center h-10 w-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            aria-label="Add card"
          >
            <i className="fa-solid fa-plus" />
          </button>
        </div>

        <div className="hidden sm:grid gap-4 w-full" style={gridStyle}>
          <div className="relative min-w-0">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
              aria-label="Search cards"
            />
          </div>
          <button
            type="button"
            onClick={openAddForm}
            style={{ gridColumn: gridColumns }}
            className="inline-flex items-center justify-center gap-2 h-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 text-sm font-medium justify-self-end"
            aria-label="Add card"
          >
            <i className="fa-solid fa-plus" />
            Add card
          </button>
        </div>
      </header>

      {loading && (
        <p className="text-center text-gray-600 py-8">Loading cards...</p>
      )}

      {error && (
        <p className="text-center text-red-600 py-8">{error}</p>
      )}

      {!loading && !error && filteredCards.length === 0 && (
        <p className="text-center text-gray-600 py-8">No cards found.</p>
      )}

      {!loading && !error && filteredCards.length > 0 && (
        <div className="grid gap-4 w-full" style={gridStyle}>
          {filteredCards.map((card) => (
            <BankCard
              key={card.id}
              card={card}
              onEdit={openEditForm}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      <CardFormModal
        isOpen={formModal.open}
        mode={formModal.mode}
        initialValues={formModal.card}
        onClose={closeFormModal}
        onSave={handleSave}
        saving={saving}
        error={formError}
      />

      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete card"
        message={`Are you sure you want to delete "${deleteModal.card?.title}"? This action cannot be undone.`}
        error={deleteError}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteModal}
        variant="danger"
        loading={deleting}
      />
    </>
  );
};

export default CardsList;
